const QR_FRAME_PADDING_RATIO = 0.2;
const QR_FRAME_MIN_SIZE = 96;
const QR_FRAME_EDGE_INSET = 8;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getPointCoordinate = (point, axis) => {
    const getter = axis === "x" ? "getX" : "getY";

    if (typeof point?.[getter] === "function") {
        return Number(point[getter]());
    }

    return Number(point?.[axis]);
};

export const calculateAttendanceQrFrameBounds = ({
        points,
        videoWidth,
        videoHeight,
        videoRect,
        cameraRect
    }) => {
    if (
        !Array.isArray(points) ||
        points.length < 2 ||
        !videoWidth ||
        !videoHeight ||
        !videoRect?.width ||
        !videoRect?.height ||
        !cameraRect?.width ||
        !cameraRect?.height
    ) {
        return null;
    }

    const scale = Math.max(
        videoRect.width / videoWidth,
        videoRect.height / videoHeight
    );
    const renderedWidth = videoWidth * scale;
    const renderedHeight = videoHeight * scale;
    const renderedOffsetX = (videoRect.width - renderedWidth) / 2;
    const renderedOffsetY = (videoRect.height - renderedHeight) / 2;

    const displayPoints = points.map((point) => {
        const x = getPointCoordinate(point, "x");
        const y = getPointCoordinate(point, "y");

        if (!Number.isFinite(x) || !Number.isFinite(y)) {
            return null;
        }

        return {
            x: videoRect.left + renderedOffsetX + (x * scale) - cameraRect.left,
            y: videoRect.top + renderedOffsetY + (y * scale) - cameraRect.top
        };
    }).filter(Boolean);

    if (displayPoints.length < 2) {
        return null;
    }

    const xValues = displayPoints.map(({ x }) => x);
    const yValues = displayPoints.map(({ y }) => y);
    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);
    const rawWidth = maxX - minX;
    const rawHeight = maxY - minY;

    if (rawWidth <= 0 || rawHeight <= 0) {
        return null;
    }

    const padding = Math.max(rawWidth, rawHeight) * QR_FRAME_PADDING_RATIO;
    const availableWidth = Math.max(0, cameraRect.width - (QR_FRAME_EDGE_INSET * 2));
    const availableHeight = Math.max(0, cameraRect.height - (QR_FRAME_EDGE_INSET * 2));
    const size = Math.min(
        availableWidth,
        availableHeight,
        Math.max(
            QR_FRAME_MIN_SIZE,
            Math.max(rawWidth, rawHeight) + (padding * 2)
        )
    );
    const width = size;
    const height = size;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const left = clamp(
        centerX - (width / 2),
        QR_FRAME_EDGE_INSET,
        Math.max(QR_FRAME_EDGE_INSET, cameraRect.width - QR_FRAME_EDGE_INSET - width)
    );
    const top = clamp(
        centerY - (height / 2),
        QR_FRAME_EDGE_INSET,
        Math.max(QR_FRAME_EDGE_INSET, cameraRect.height - QR_FRAME_EDGE_INSET - height)
    );

    return {
        left: Math.round(left),
        top: Math.round(top),
        width: Math.round(width),
        height: Math.round(height)
    };
};

export const getAttendanceQrFrameBounds = (result, videoElement, cameraElement) => {
    if (!result || !videoElement || !cameraElement) {
        return null;
    }

    return calculateAttendanceQrFrameBounds({
        points: result.getResultPoints?.(),
        videoWidth: videoElement.videoWidth,
        videoHeight: videoElement.videoHeight,
        videoRect: videoElement.getBoundingClientRect(),
        cameraRect: cameraElement.getBoundingClientRect()
    });
};
